import config from '../config/config';

class LoggingService {
  static getBaseUrl() {
    return config.API_BASE_URL;
  }

  static logLevels = {
    INFO: "ℹ️",
    SUCCESS: "✅",
    WARNING: "⚠️",
    ERROR: "❌",
    SECURITY: "🔒",
    SYSTEM: "⚙️"
  };


  static verifyToken() {
    const token = localStorage.getItem('userToken'); // Changed from 'token' to 'userToken'
    return token != null;
  }

  static getAuthHeaders() {
    const token = localStorage.getItem('userToken'); // Changed from 'token' to 'userToken'
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  static mapTypeToLevel(type) {
    const levelMap = {
      'INFO': 'info',
      'SUCCESS': 'info',
      'WARNING': 'warning',
      'ERROR': 'error',
      'SECURITY': 'info',
      'SYSTEM': 'info'
    };
    return levelMap[type] || 'info';
  }

  static logAuth(action, success, details = "") {
    const icon = success ? this.logLevels.SUCCESS : this.logLevels.ERROR;
    const message = `${icon} Authentication ${action}: ${details}`;
    this.log("AUTH", message);
  }

  static logNavigation(path) {
    this.log("NAV", `User navigated to ${path}`);
  }

  static logAction(action, details) {
    this.log("ACTION", `${this.logLevels.INFO} ${action}: ${details}`);
  }

  static logSecurity(event, details) {
    this.log("SECURITY", `${this.logLevels.SECURITY} Security event - ${event}: ${details}`);
  }

  static logError(error, context) {
    this.log("ERROR", `${this.logLevels.ERROR} Error in ${context}: ${error.message}`);
  }

  static logSystem(event, details) {
    this.log("SYSTEM", `${this.logLevels.SYSTEM} System event - ${event}: ${details}`);
  }

  static async log(type, message, user = null) {
    const timestamp = new Date().toLocaleString();
    const userEmail = user || localStorage.getItem("userEmail") || "Unknown User";

    // Create log entry for both local and server storage
    const logEntry = {
      timestamp,
      type,
      message
    };

    // Store locally
    let logs = this.getLogs();
    if (!logs[userEmail]) {
      logs[userEmail] = [];
    }
    logs[userEmail].push(logEntry);
    localStorage.setItem("logs", JSON.stringify(logs));

    // Send to server only if authenticated
    if (!this.verifyToken()) return;

    try {
      await fetch(`${this.getBaseUrl()}/api/logs`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          userId: userEmail,
          action: type,
          details: message,
          options: {
            level: this.mapTypeToLevel(type),
            category: 'client',
            metadata: {
              userAgent: navigator.userAgent,
              timestamp
            }
          }
        })
      });
    } catch (error) {
      console.error('Failed to send log to server:', error);
    }
  }

  static async getLogs() {
    const localLogs = JSON.parse(localStorage.getItem("logs")) || {};

    if (!this.verifyToken()) return localLogs;

    try {
      const response = await fetch(`${this.getBaseUrl()}/api/logs`, {
        headers: this.getAuthHeaders()
      });

      if (response.ok) {
        const serverLogs = await response.json();
        return { ...localLogs, ...serverLogs };
      }
    } catch (error) {
      console.error('Failed to fetch server logs:', error);
    }

    return localLogs;
  }

  static async verifyLoggingSetup() {
    const results = {
      tokenExists: false,
      endpointGet: false,
      endpointPost: false,
      formatHandling: false
    };

    results.tokenExists = this.verifyToken();

    try {
      const getResponse = await fetch(`${this.getBaseUrl()}/api/logs`, {
        headers: this.getAuthHeaders() // Use getAuthHeaders instead of direct token access
      });
      results.endpointGet = getResponse.ok;

      results.formatHandling = await this.testLogFormat();
      results.endpointPost = results.formatHandling;

    } catch (error) {
      console.error('Logging setup verification failed:', error);
    }

    return results;
  }

  // Change to static method
  static testLogFormat() {
    try {
      // Basic log format test
      const testLog = {
        level: 'info',
        message: 'test',
        timestamp: new Date().toISOString()
      };
      return true;
    } catch (error) {
      return false;
    }
  }
}

export default LoggingService;
